import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Navbar from '../../components/public/Navbar';
import Footer from '../../components/public/Footer';
import GemCard from '../../components/public/GemCard';
import api from '../../utils/api';

// ── useReveal ─────────────────────────────────────────────────────
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

// ── Gem3D ─────────────────────────────────────────────────────────
function Gem3D({ size = 340, glowColor = 'rgba(30,100,255,0.9)' }) {
  const glowMid  = glowColor.replace('0.9)', '0.5)');
  const glowSoft = glowColor.replace('0.9)', '0.2)');

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0, margin: '0 auto' }}>
      {/* SVG filter — removes white background */}
      <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
        <defs>
          <filter id="gem3d-rm-white" colorInterpolationFilters="sRGB" x="-5%" y="-5%" width="110%" height="110%">
            <feColorMatrix type="matrix" result="mask"
              values="1 0 0 0 0
                      0 1 0 0 0
                      0 0 1 0 0
                     -1.5 -1.5 -1.5 0 3.8" />
            <feComposite in="SourceGraphic" in2="mask" operator="in" />
          </filter>
        </defs>
      </svg>

      {/* Deep background glow */}
      <div style={{
        position: 'absolute', inset: '-40%',
        background: `radial-gradient(ellipse, ${glowMid} 0%, ${glowSoft} 40%, transparent 68%)`,
        filter: 'blur(28px)',
        animation: 'gemPulse 3s ease-in-out infinite',
      }} />

      {/* Gem image */}
      <img
        src="/gem-hero.png"
        alt="Gem"
        style={{
          position: 'absolute', inset: 0, zIndex: 1,
          width: '100%', height: '100%',
          objectFit: 'contain',
          filter: `url(#gem3d-rm-white) drop-shadow(0 0 28px ${glowColor}) drop-shadow(0 0 65px ${glowMid}) drop-shadow(0 0 110px ${glowSoft})`,
          animation: 'gem-spin-z 28s linear infinite',
        }}
        onError={e => { e.target.style.display = 'none'; }}
      />

      {/* Facet reflections — sparkles at gem facet positions, tinted to glow colour */}
      {[
        { top: '14%',  left: '52%', size: '22%', delay: '0s',   dur: '4.2s' },
        { top: '42%',  right: '8%', size: '17%', delay: '1.6s', dur: '3.7s' },
        { bottom:'14%',left: '30%', size: '19%', delay: '2.9s', dur: '4.5s' },
        { top: '28%',  left: '8%',  size: '15%', delay: '0.8s', dur: '4.0s' },
        { bottom:'28%',right:'16%', size: '13%', delay: '3.6s', dur: '3.5s' },
        { top: '62%',  left: '18%', size: '11%', delay: '2.1s', dur: '4.8s' },
      ].map((s, i) => (
        <div key={i} style={{
          position: 'absolute',
          width: s.size, height: s.size,
          top: s.top, left: s.left, right: s.right, bottom: s.bottom,
          borderRadius: '50%',
          background: `radial-gradient(circle, rgba(255,255,255,0.92) 0%, ${glowMid} 35%, transparent 70%)`,
          zIndex: 2, pointerEvents: 'none',
          animation: `facetFlash ${s.dur} ease-in-out infinite ${s.delay}`,
        }} />
      ))}
    </div>
  );
}

// ── SectionHead ───────────────────────────────────────────────────
function SectionHead({ eyebrow, title, desc, align = 'center' }) {
  const [ref, visible] = useReveal();
  return (
    <div ref={ref} className={`reveal${visible ? ' in' : ''}`} style={{ textAlign: align, marginBottom: 56 }}>
      {eyebrow && (
        <p style={{ fontSize: 10.5, letterSpacing: '3.5px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 14, fontFamily: 'var(--font-sans)' }}>
          {eyebrow}
        </p>
      )}
      <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem,4vw,3.2rem)', fontWeight: 300, color: 'var(--white)', lineHeight: 1.18, marginBottom: 18 }}>
        {title}
      </h2>
      <div style={{ width: 50, height: 1, background: 'var(--gold)', margin: align === 'center' ? '0 auto' : '0' }} />
      {desc && (
        <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.85, maxWidth: 560, margin: align === 'center' ? '22px auto 0' : '22px 0 0' }}>
          {desc}
        </p>
      )}
    </div>
  );
}

// ── Hero ──────────────────────────────────────────────────────────
const FALLBACK_SLIDES = [
  { id: 1, image_url: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=1800&q=90', title: 'The Rarest Gems on Earth', subtitle: 'Ethically sourced from the ancient mines of Sri Lanka — certified natural gemstones delivered anywhere in the world.', cta_text: 'Explore Collection', cta_link: '/shop' },
  { id: 2, image_url: 'https://images.unsplash.com/photo-1611955167811-4711904bb9f8?w=1800&q=90', title: 'Padparadscha Sapphire', subtitle: "Nature's most coveted gemstone — the sacred sunset sapphire of Ceylon. Each stone is one of a kind.", cta_text: 'View Sapphires', cta_link: '/shop?category=sapphire' },
];

function Hero({ slides }) {
  const [scrollY, setScrollY]   = useState(0);
  const [slideIdx, setSlideIdx] = useState(0);
  const timerRef = useRef();

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (i) => {
    setSlideIdx(i);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setSlideIdx(c => (c + 1) % slides.length), 7000);
  };

  useEffect(() => {
    timerRef.current = setInterval(() => setSlideIdx(c => (c + 1) % slides.length), 7000);
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  const slide = slides[slideIdx] || slides[0];

  return (
    <section style={{ position: 'relative', height: '100vh', minHeight: 600, background: '#07070a', overflow: 'hidden' }}>

      {/* Background images — zIndex 0 */}
      {slides.map((s, i) => (
        <img key={s.id} src={s.image_url} alt=""
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center',
            opacity: i === slideIdx ? 1 : 0,
            transition: 'opacity 1.6s ease',
            pointerEvents: 'none', zIndex: 0,
          }} />
      ))}

      {/* Overlays — zIndex 1 */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: 'rgba(7,7,10,0.42)', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', background: 'linear-gradient(to right, rgba(7,7,10,0.78) 0%, rgba(7,7,10,0.28) 50%, rgba(7,7,10,0) 100%)' }} />
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 220, zIndex: 1, pointerEvents: 'none', background: 'linear-gradient(to top, rgba(7,7,10,0.95) 0%, transparent 100%)' }} />

      {/* SVG filter */}
      <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', zIndex: 0 }} aria-hidden="true">
        <defs>
          <filter id="hero-rm-white" colorInterpolationFilters="sRGB" x="-5%" y="-5%" width="110%" height="110%">
            <feColorMatrix type="matrix" result="mask"
              values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  -1.5 -1.5 -1.5 0 3.8" />
            <feComposite in="SourceGraphic" in2="mask" operator="in" />
          </filter>
        </defs>
      </svg>

      {/* Content layer — position:absolute fills hero exactly, stat rail always at bottom */}
      <div className="hero-content" style={{
        position: 'absolute', inset: 0, zIndex: 6,
        display: 'flex', flexDirection: 'column',
        paddingTop: 100, paddingBottom: 28,
        paddingLeft: 'clamp(20px, 6vw, 100px)', paddingRight: 'clamp(20px, 6vw, 100px)',
      }}>

        {/* Main row: text left, gem right — equal columns, scales on any screen */}
        <div className="hero-main-row" style={{ flex: 1, minHeight: 0 }}>

          {/* Text */}
          <div key={slideIdx} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <p style={{ fontSize: 10.5, letterSpacing: '4px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 22, fontFamily: 'var(--font-sans)', animation: 'fadeUp 0.7s ease both' }}>
              Ceylon · Sri Lanka · Est. 1989
            </p>
            <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2.4rem, 4.2vw, 5rem)', fontWeight: 300, color: '#fff', lineHeight: 1.06, marginBottom: 24, animation: 'fadeUp 0.75s ease 0.08s both' }}>
              {slide.title}
            </h1>
            <div style={{ width: 60, height: 1, background: 'var(--gold)', marginBottom: 24, animation: 'fadeUp 0.75s ease 0.16s both' }} />
            <p className="hero-subtitle" style={{ fontSize: 15, color: 'rgba(246,241,231,0.75)', lineHeight: 1.85, maxWidth: 480, marginBottom: 28, animation: 'fadeUp 0.75s ease 0.22s both' }}>
              {slide.subtitle}
            </p>
            <div className="hero-btns" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', animation: 'fadeUp 0.75s ease 0.3s both' }}>
              <Link to={slide.cta_link || '/shop'} className="btn btn-gold btn-lg">{slide.cta_text || 'Explore Collection'}</Link>
              <Link to="/contact" className="btn btn-outline btn-lg hero-btn-secondary">Speak to a Gemologist</Link>
            </div>

            {slides.length > 1 && (
              <div className="hero-controls" style={{ display: 'flex', alignItems: 'center', gap: 14, marginTop: 28, animation: 'fadeUp 0.75s ease 0.38s both' }}>
                <button onClick={() => go((slideIdx - 1 + slides.length) % slides.length)}
                  style={{ width: 38, height: 38, borderRadius: '50%', border: '1px solid rgba(246,241,231,0.25)', background: 'rgba(7,7,10,0.5)', color: 'rgba(246,241,231,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.25s ease', flexShrink: 0 }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.color = 'var(--gold)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(246,241,231,0.25)'; e.currentTarget.style.color = 'rgba(246,241,231,0.7)'; }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6"/></svg>
                </button>
                {slides.map((_, i) => (
                  <button key={i} onClick={() => go(i)} style={{ width: i === slideIdx ? 28 : 8, height: 8, borderRadius: 4, border: 'none', background: i === slideIdx ? 'var(--gold)' : 'rgba(246,241,231,0.2)', transition: 'all 0.4s ease', cursor: 'pointer' }} />
                ))}
                <button onClick={() => go((slideIdx + 1) % slides.length)}
                  style={{ width: 38, height: 38, borderRadius: '50%', border: '1px solid rgba(246,241,231,0.25)', background: 'rgba(7,7,10,0.5)', color: 'rgba(246,241,231,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', transition: 'all 0.25s ease', flexShrink: 0 }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--gold)'; e.currentTarget.style.color = 'var(--gold)'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(246,241,231,0.25)'; e.currentTarget.style.color = 'rgba(246,241,231,0.7)'; }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                </button>
              </div>
            )}
          </div>

          {/* Gem — right column, centers itself, scales with viewport width */}
          <div className="gem3d-wrap" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ position: 'relative', width: 'clamp(260px, 28vw, 580px)', height: 'clamp(260px, 28vw, 580px)' }}>
              {/* Deep background glow */}
              <div style={{ position: 'absolute', inset: '-40%', background: 'radial-gradient(ellipse, rgba(30,90,255,0.45) 0%, rgba(30,90,255,0.15) 45%, transparent 70%)', filter: 'blur(30px)', animation: 'gemPulse 3s ease-in-out infinite' }} />

              {/* Gem image */}
              <img src="/gem-hero.png" alt="Blue Sapphire"
                style={{ position: 'absolute', inset: 0, zIndex: 1, width: '100%', height: '100%', objectFit: 'contain', filter: 'url(#hero-rm-white) drop-shadow(0 0 30px rgba(30,100,255,0.95)) drop-shadow(0 0 70px rgba(30,100,255,0.6)) drop-shadow(0 0 120px rgba(30,100,255,0.3))', animation: 'gem-spin-z 28s linear infinite' }}
                onError={e => { e.target.style.opacity = '0'; }}
              />

              {/* Facet reflections — individual sparkles at gem facet positions */}
              {[
                { top: '14%',  left: '52%', size: '22%', delay: '0s',    dur: '4.2s' },
                { top: '42%',  right: '8%', size: '17%', delay: '1.6s',  dur: '3.7s' },
                { bottom:'14%',left: '30%', size: '19%', delay: '2.9s',  dur: '4.5s' },
                { top: '28%',  left: '8%',  size: '15%', delay: '0.8s',  dur: '4.0s' },
                { bottom:'28%',right:'16%', size: '13%', delay: '3.6s',  dur: '3.5s' },
                { top: '62%',  left: '18%', size: '11%', delay: '2.1s',  dur: '4.8s' },
              ].map((s, i) => (
                <div key={i} style={{
                  position: 'absolute',
                  width: s.size, height: s.size,
                  top: s.top, left: s.left, right: s.right, bottom: s.bottom,
                  borderRadius: '50%',
                  background: 'radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(220,235,255,0.45) 30%, transparent 70%)',
                  zIndex: 2, pointerEvents: 'none',
                  animation: `facetFlash ${s.dur} ease-in-out infinite ${s.delay}`,
                }} />
              ))}
            </div>
          </div>
        </div>

        {/* Stat rail — always pinned to bottom of content layer */}
        <div className="stat-rail" style={{ borderTop: '1px solid rgba(246,241,231,0.1)', paddingTop: 22, marginTop: 0 }}>
          {[['500+', 'Certified Gems'], ['35+', 'Years Heritage'], ['60+', 'Countries'], ['100%', 'Natural & Unheated']].map(([num, label]) => (
            <div key={label} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.4rem,2.8vw,2.2rem)', fontWeight: 300, color: 'var(--gold)', fontVariantNumeric: 'lining-nums tabular-nums' }}>{num}</div>
              <div style={{ fontSize: 9, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'rgba(246,241,231,0.45)', marginTop: 4 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <div style={{ position: 'absolute', bottom: 36, right: 6, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, zIndex: 10 }}>
        <span style={{ fontSize: 9, letterSpacing: 3, textTransform: 'uppercase', color: 'rgba(246,241,231,0.3)', writingMode: 'vertical-rl' }}>Scroll</span>
        <div style={{ width: 1, height: 44, background: 'linear-gradient(to bottom, var(--gold), transparent)' }} />
      </div>
    </section>
  );
}

// ── FeaturedSection ───────────────────────────────────────────────
function FeaturedSection({ gems, loading }) {
  const [ref, visible] = useReveal();
  return (
    <section className="section" style={{ background: 'var(--black)' }}>
      <div className="container">
        <SectionHead
          eyebrow="Curated Selection"
          title="Featured Collection"
          desc="Each gemstone is personally selected by our master gemologists for exceptional quality, colour saturation, and natural origin from the mines of Sri Lanka."
        />
        <div ref={ref} className={`gems-grid reveal${visible ? ' in' : ''}`}>
          {loading
            ? [...Array(8)].map((_, i) => <div key={i} className="skeleton" style={{ height: 420, borderRadius: 4 }} />)
            : gems.map(gem => <GemCard key={gem.id} gem={gem} />)
          }
        </div>
        <div style={{ textAlign: 'center', marginTop: 52 }}>
          <Link to="/shop" className="btn btn-outline-gold btn-lg">View Entire Collection</Link>
        </div>
      </div>
    </section>
  );
}

// ── AlexandriteSection ────────────────────────────────────────────
function AlexandriteSection() {
  const [mode, setMode] = useState('day');
  const [ref, visible] = useReveal();

  const isDay = mode === 'day';

  return (
    <section className="section" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
      <div className="container">
        <div ref={ref} className="two-col">
          {/* Text */}
          <div className={`reveal${visible ? ' in' : ''}`}>
            <p style={{ fontSize: 10.5, letterSpacing: '3.5px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 16, fontFamily: 'var(--font-sans)' }}>The Miracle Stone</p>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem,4vw,3.2rem)', fontWeight: 300, color: 'var(--white)', lineHeight: 1.18, marginBottom: 18 }}>
              Alexandrite —<br />Nature's Colour Magic
            </h2>
            <div style={{ width: 50, height: 1, background: 'var(--gold)', marginBottom: 24 }} />
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.9, marginBottom: 14 }}>
              Alexandrite is one of nature's rarest phenomena — a gemstone that displays a dramatic colour change from vivid green in daylight to rich red-purple under incandescent light.
            </p>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.9, marginBottom: 32 }}>
              Ceylon alexandrite is world-famous for producing the strongest colour change of any source. Our stones are unheated and come with full gemological certification.
            </p>

            {/* Light toggle */}
            <div style={{ display: 'inline-flex', background: 'rgba(246,241,231,0.05)', border: '1px solid var(--border2)', borderRadius: 999, padding: 4, gap: 4, marginBottom: 32 }}>
              {[['day', 'Daylight'], ['night', 'Candlelight']].map(([m, label]) => (
                <button key={m} onClick={() => setMode(m)} style={{
                  padding: '9px 22px', borderRadius: 999, border: 'none',
                  background: mode === m ? 'var(--gold)' : 'transparent',
                  color: mode === m ? '#07070a' : 'var(--muted)',
                  fontSize: 11, letterSpacing: '1.5px', textTransform: 'uppercase',
                  cursor: 'pointer', transition: 'all 0.3s ease',
                  fontFamily: 'var(--font-sans)', fontWeight: 500,
                }}>{label}</button>
              ))}
            </div>

            <br />
            <Link to="/shop?category=alexandrite" className="btn btn-gold">Explore Alexandrite</Link>
          </div>

          {/* Gem card */}
          <div className={`reveal delay-2${visible ? ' in' : ''}`}>
            <div style={{
              background: isDay
                ? 'linear-gradient(135deg, rgba(22,163,74,0.12), rgba(7,7,10,0.9))'
                : 'linear-gradient(135deg, rgba(124,58,237,0.12), rgba(7,7,10,0.9))',
              border: `1px solid ${isDay ? 'rgba(22,163,74,0.3)' : 'rgba(124,58,237,0.3)'}`,
              borderRadius: 8, padding: '48px 32px', transition: 'all 0.8s ease',
            }}>
              <Gem3D size={260} glowColor={isDay ? 'rgba(34,197,94,0.9)' : 'rgba(168,85,247,0.9)'} />
              <div style={{ textAlign: 'center', marginTop: 28 }}>
                <p style={{ fontFamily: 'var(--font-serif)', fontSize: 20, color: 'var(--white)', fontWeight: 300, marginBottom: 6 }}>Ceylon Alexandrite</p>
                <p style={{ fontSize: 10, letterSpacing: '2.5px', textTransform: 'uppercase', color: isDay ? '#22c55e' : '#a855f7', transition: 'color 0.5s ease' }}>
                  {isDay ? '— In Daylight —' : '— Under Candlelight —'}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── OriginSection ─────────────────────────────────────────────────
function OriginSection() {
  const [scrollY, setScrollY] = useState(0);
  const [ref, visible]        = useReveal();

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section style={{ position: 'relative', padding: '120px 0', overflow: 'hidden' }}>
      {/* Parallax background */}
      <div style={{ position: 'absolute', inset: '-20% 0', transform: `translateY(${scrollY * 0.12}px)`, willChange: 'transform', pointerEvents: 'none' }}>
        <img src="https://images.unsplash.com/photo-1578301978693-85fa9c0320b9?w=1800&q=85" alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.2)' }} />
      </div>
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, rgba(7,7,10,0.88) 0%, rgba(7,7,10,0.72) 100%)', pointerEvents: 'none' }} />

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <div ref={ref} className="two-col">
          {/* Stats grid */}
          <div className={`origin-stats reveal${visible ? ' in' : ''}`}>
            {[
              ['2,500+', 'Years of gem mining heritage in Sri Lanka'],
              ['Ratnapura', 'The City of Gems — world\'s finest sapphires'],
              ['GIA · AGL · GRS', 'International certification bodies'],
              ['60+', 'Countries served with worldwide shipping'],
            ].map(([num, label], i) => (
              <div key={i} style={{ padding: '28px 24px', background: 'rgba(246,241,231,0.04)', border: '1px solid rgba(246,241,231,0.08)', borderRadius: 4 }}>
                <div style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.1rem, 2.2vw, 1.8rem)', fontWeight: 300, color: 'var(--gold)', marginBottom: 8, lineHeight: 1.2 }}>{num}</div>
                <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.65 }}>{label}</div>
              </div>
            ))}
          </div>

          {/* Text */}
          <div className={`reveal delay-2${visible ? ' in' : ''}`}>
            <p style={{ fontSize: 10.5, letterSpacing: '3.5px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 16, fontFamily: 'var(--font-sans)' }}>The Origin</p>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem,4vw,3.2rem)', fontWeight: 300, color: 'var(--white)', lineHeight: 1.18, marginBottom: 18 }}>
              Born from the Ancient Earth of Ceylon
            </h2>
            <div style={{ width: 50, height: 1, background: 'var(--gold)', marginBottom: 24 }} />
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.9, marginBottom: 14 }}>
              Sri Lanka — once known as Ceylon — has been the world's most celebrated source of precious gemstones for over 2,500 years. From the legendary mines of Ratnapura to the valley floors of Elahera, the island's ancient geology produces sapphires, rubies, and alexandrite of unmatched beauty.
            </p>
            <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.9, marginBottom: 36 }}>
              Every gem in our collection carries a full provenance record. We mine, certify, and ship directly to collectors and connoisseurs across the world — eliminating the middleman.
            </p>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              <Link to="/about" className="btn btn-gold">Our Heritage</Link>
              <Link to="/contact" className="btn btn-outline">Talk to a Gemologist</Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── CategoriesSection ─────────────────────────────────────────────
function CategoriesSection() {
  const [ref, visible] = useReveal();
  const [cats, setCats] = useState(null); // null = loading

  useEffect(() => {
    api.get('/categories')
      .then(r => setCats(r.data.data || []))
      .catch(() => setCats([]));
  }, []);

  if (cats !== null && cats.length === 0) return null;

  return (
    <section className="section" style={{ background: 'var(--black)' }}>
      <div className="container">
        <SectionHead eyebrow="Browse by Gem" title="Our Collections" />
        <div ref={ref} className={`cat-grid reveal${visible ? ' in' : ''}`}>
          {cats === null
            ? [...Array(4)].map((_, i) => (
                <div key={i} className="skeleton" style={{ aspectRatio: '4/5', borderRadius: 4 }} />
              ))
            : cats.map(cat => (
            <Link key={cat.slug} to={`/shop?category=${cat.slug}`} style={{ textDecoration: 'none', display: 'block' }}>
              <div style={{ position: 'relative', aspectRatio: '4/5', overflow: 'hidden', borderRadius: 4, cursor: 'pointer', background: 'var(--surface2)' }}
                onMouseEnter={e => {
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.style.transform = 'scale(1.08)';
                  e.currentTarget.querySelector('.cat-ov').style.opacity = '1';
                }}
                onMouseLeave={e => {
                  const img = e.currentTarget.querySelector('img');
                  if (img) img.style.transform = 'scale(1)';
                  e.currentTarget.querySelector('.cat-ov').style.opacity = '0';
                }}>
                {cat.image ? (
                  <img src={cat.image} alt={cat.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.7s ease', filter: 'brightness(0.55)' }} />
                ) : (
                  <div style={{ width: '100%', height: '100%', background: 'linear-gradient(135deg, var(--surface2), var(--surface3))' }} />
                )}
                <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, transparent 55%)' }} />
                <div className="cat-ov" style={{ position: 'absolute', inset: 0, background: 'rgba(212,176,106,0.13)', opacity: 0, transition: 'opacity 0.35s' }} />
                <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '24px 20px' }}>
                  <p style={{ fontFamily: 'var(--font-serif)', fontSize: 20, color: 'var(--white)', fontWeight: 300, marginBottom: 4 }}>{cat.name}</p>
                  {cat.gem_count > 0 && (
                    <p style={{ fontSize: 9.5, color: 'var(--muted)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: 4 }}>{cat.gem_count} gems</p>
                  )}
                  <span style={{ fontSize: 9.5, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'var(--gold)' }}>Shop Now →</span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── TrustSection ──────────────────────────────────────────────────
function TrustSection() {
  const [ref, visible] = useReveal();
  const pillars = [
    { num: '01', title: 'GIA Certified', desc: 'Every gemstone comes with an internationally recognised gemological certificate from GIA, AGL, or GRS.' },
    { num: '02', title: 'Ethically Sourced', desc: 'Direct relationships with licensed Sri Lankan miners. No exploitation, no conflict gems — ever.' },
    { num: '03', title: 'Master Graded', desc: 'All stones are graded by our in-house master gemologists with 35+ years of Ceylon gem expertise.' },
    { num: '04', title: 'Worldwide Delivery', desc: 'Fully insured, tracked international shipping to over 60 countries. Customs documentation included.' },
  ];
  return (
    <section className="section" style={{ background: 'var(--surface)', borderTop: '1px solid var(--border)' }}>
      <div className="container">
        <SectionHead eyebrow="Our Promise" title="Why Collectors Trust Us" />
        <div ref={ref} className={`trust-grid reveal${visible ? ' in' : ''}`}>
          {pillars.map(({ num, title, desc }) => (
            <div key={num} style={{ padding: '32px 22px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--black)', transition: 'border-color 0.3s, transform 0.3s' }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(212,176,106,0.38)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}>
              <div style={{ fontFamily: 'var(--font-serif)', fontSize: 44, fontWeight: 300, color: 'var(--gold)', opacity: 0.65, lineHeight: 1, marginBottom: 18, fontVariantNumeric: 'lining-nums tabular-nums' }}>{num}</div>
              <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 400, color: 'var(--white)', marginBottom: 10 }}>{title}</h3>
              <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.8 }}>{desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── TestimonialSection ────────────────────────────────────────────
function TestimonialSection({ testimonials }) {
  const [idx, setIdx]  = useState(0);
  const [ref, visible] = useReveal();

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % testimonials.length), 5500);
    return () => clearInterval(t);
  }, [testimonials.length]);

  const t = testimonials[idx];
  if (!t) return null;

  return (
    <section className="section" style={{ background: 'var(--black)', borderTop: '1px solid var(--border)' }}>
      <div className="container">
        <SectionHead eyebrow="Client Stories" title="What Our Collectors Say" />
        <div ref={ref} className={`reveal${visible ? ' in' : ''}`} style={{ textAlign: 'center', maxWidth: 700, margin: '0 auto', padding: '0 20px' }}>
          <div style={{ fontSize: 72, color: 'var(--gold)', opacity: 0.25, fontFamily: 'Georgia', lineHeight: 0.8, marginBottom: 28 }}>"</div>
          <p key={idx} style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.05rem,2.5vw,1.32rem)', fontStyle: 'italic', color: 'var(--white)', lineHeight: 1.78, marginBottom: 32, animation: 'fadeUp 0.5s ease' }}>
            {t.message}
          </p>
          <div style={{ width: 40, height: 1, background: 'var(--gold)', margin: '0 auto 18px' }} />
          <p style={{ fontSize: 11, letterSpacing: '2.5px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 4 }}>{t.name}</p>
          <p style={{ fontSize: 11, color: 'var(--muted)' }}>{t.country}</p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 28 }}>
            {testimonials.map((_, i) => (
              <button key={i} onClick={() => setIdx(i)} style={{
                width: i === idx ? 24 : 8, height: 8, borderRadius: 4, border: 'none',
                background: i === idx ? 'var(--gold)' : 'rgba(246,241,231,0.14)',
                transition: 'all 0.3s ease', cursor: 'pointer',
              }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── CTASection ────────────────────────────────────────────────────
function CTASection() {
  const [ref, visible] = useReveal();
  return (
    <section style={{ position: 'relative', padding: '140px 0', overflow: 'hidden', textAlign: 'center' }}>
      <img src="https://images.unsplash.com/photo-1515734300980-79e0f5d08166?w=1800&q=80" alt=""
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', filter: 'brightness(0.14)' }} />
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 50% 40%, rgba(212,176,106,0.08) 0%, rgba(7,7,10,0.75) 65%)' }} />

      <div ref={ref} className={`container reveal${visible ? ' in' : ''}`} style={{ position: 'relative', zIndex: 1 }}>
        <p style={{ fontSize: 10.5, letterSpacing: '4px', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 20, fontFamily: 'var(--font-sans)' }}>
          Custom Orders Welcome
        </p>
        <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem,5vw,4rem)', fontWeight: 300, color: 'var(--white)', marginBottom: 24, lineHeight: 1.18 }}>
          Looking for Something<br />Exceptional?
        </h2>
        <div style={{ width: 50, height: 1, background: 'var(--gold)', margin: '0 auto 28px' }} />
        <p style={{ fontSize: 15, color: 'rgba(246,241,231,0.62)', maxWidth: 520, margin: '0 auto 48px', lineHeight: 1.88 }}>
          Our gemologists work with private collectors, jewellers, and investors to source one-of-a-kind Ceylon gems. Share your requirements and we will find it.
        </p>
        <Link to="/contact" className="btn btn-gold btn-lg">Contact Our Gemologists</Link>
      </div>
    </section>
  );
}

// ── HomePage ──────────────────────────────────────────────────────
export default function HomePage() {
  const [slides,       setSlides]       = useState(FALLBACK_SLIDES);
  const [featured,     setFeatured]     = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/sliders').catch(() => ({ data: { data: [] } })),
      api.get('/gems/list?featured=1&limit=8').catch(() => ({ data: { data: [] } })),
      api.get('/testimonials').catch(() => ({ data: { data: [] } })),
    ]).then(([s, g, t]) => {
      if (s.data.data?.length) setSlides(s.data.data);
      setFeatured(g.data.data || []);
      setTestimonials(t.data.data || []);
    }).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <Helmet>
        <title>Sudath Gems | Finest Natural Gemstones from Sri Lanka</title>
        <meta name="description" content="Buy certified natural Ceylon sapphires, rubies, alexandrite & rare gems directly from Sri Lanka. GIA certified, unheated, ethically sourced. Worldwide shipping in USD." />
        <meta property="og:title" content="Sudath Gems | Finest Natural Gemstones from Sri Lanka" />
        <meta property="og:type" content="website" />
      </Helmet>
      <Navbar />
      <Hero slides={slides} />
      <FeaturedSection gems={featured} loading={loading} />
      <AlexandriteSection />
      <OriginSection />
      <CategoriesSection />
      <TrustSection />
      {testimonials.length > 0 && <TestimonialSection testimonials={testimonials} />}
      <CTASection />
      <Footer />
    </>
  );
}
